"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { ButtonRadialFill } from "@/components/button-radial-fill";
import { IconBox } from "@/components/icon-box";
import { usePageTransition } from "@/components/page-transition-provider";
import Avatars from "~/public/assets/avatars.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";

type DynamicsServicesSectionProps = {
	service: ServicePageContent;
};

type DynamicsCardProps = {
	card: NonNullable<ServicePageContent["dynamics"]>["cards"][number];
	iconVariant?: ServicePageContent["iconVariant"];
};

function DynamicsCard({ card, iconVariant }: DynamicsCardProps) {
	return (
		<div
			data-service-card
			className="flex h-full flex-col bg-gray-900 p-10 pb-16 md:p-16 md:pb-32"
		>
			<div data-service-card-media>
				{iconVariant === "outline" ? (
					<IconBox bordered icon={card.icon} />
				) : (
					<Image
						src={card.icon}
						alt={card.title}
						className="h-20 w-20"
						width={200}
						height={200}
					/>
				)}
			</div>

			<div className="mt-8 md:mt-24 flex flex-1 flex-col gap-6">
				<h3 className="font-bricolage text-2xl font-medium">{card.title}</h3>
				<p className="description">{card.description}</p>
			</div>
		</div>
	);
}

export default function DynamicsServicesSection({
	service,
}: DynamicsServicesSectionProps) {
	const dynamics = service.dynamics;
	const { navigate } = usePageTransition();
	const sectionRef = useRef<HTMLElement | null>(null);

	useServiceCardsAnimation(sectionRef, {
		preset: "tilt",
	});

	if (!dynamics) return null;

	return (
		<section
			ref={sectionRef}
			className="relative border-t border-gray-300 text-white bg-gray-1100"
		>
			<div className="md:container-padding">
				<div className="container relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-18 lg:py-28">
					<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />
					<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />

					<div className="max-w-120">
						<p className="tag">{dynamics.tag}</p>
						<h2 className="title">{dynamics.title}</h2>
					</div>

					<div data-service-cards-animation-trigger className="mt-12 lg:hidden">
						<Swiper
							a11y={{ enabled: true }}
							className="overflow-visible!"
							modules={[Navigation, A11y]}
							navigation={{
								prevEl: ".dynamics-prev",
								nextEl: ".dynamics-next",
							}}
							slidesPerView="auto"
							spaceBetween={20}
						>
							{dynamics.cards.map((card) => (
								<SwiperSlide className="h-auto! max-w-[92%]" key={card.title}>
									<DynamicsCard card={card} iconVariant={service.iconVariant} />
								</SwiperSlide>
							))}
						</Swiper>

						<div className="mt-5 flex items-center gap-5">
							<button
								aria-label="Card anterior"
								className="dynamics-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowLeft
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
							<button
								aria-label="Próximo card"
								className="dynamics-next cursor-pointer text-white transition-all duration-300 ease-in-out"
								type="button"
							>
								<ArrowRight
									className="size-9 text-primary-300"
									strokeWidth={1.5}
								/>
							</button>
						</div>
					</div>

					<div className="why-flowlab-cta max-w-fit mx-auto mt-16 lg:mt-150 2xl:mt-136! flex flex-col gap-16 rounded-3xl border border-primary-300/50 p-9 lg:flex-row md:items-center md:justify-between">
						<div>
							<h3 className="text-xl font-semibold leading-[140%] md:text-2xl">
								{dynamics.titleContact}
							</h3>
							<p className="mt-1 text-sm leading-[150%] text-gray-100/55">
								{dynamics.descriptionContact}
							</p>
						</div>

						<div className="flex flex-wrap items-center gap-9 md:flex-nowrap">
							<Image
								src={Avatars}
								alt="Equipe Flow"
								className="w-full max-w-42"
								width={1000}
								height={1000}
							/>

							<ButtonRadialFill
								className="h-14.5 px-6 rounded-2xl border border-primary-300 bg-primary-300"
								classNameFill="bg-gray-1100"
								onClick={(event) => {
									void navigate(dynamics.ctaContact.href, {
										origin: {
											x: event.clientX,
											y: event.clientY,
										},
									});
								}}
							>
								{dynamics.ctaContact.label}
							</ButtonRadialFill>
						</div>
					</div>
				</div>

				<div
					data-service-cards-animation-trigger
					className="absolute top-50 lg:top-70 left-1/2 hidden w-full -translate-x-1/2 gap-4 px-7 lg:grid lg:grid-cols-3"
				>
					{dynamics.cards.map((card) => (
						<DynamicsCard
							key={card.title}
							card={card}
							iconVariant={service.iconVariant}
						/>
					))}
				</div>
			</div>
		</section>
	);
}
